// @ts-nocheck
import { cancelReasonLabel } from "@kawkaw/shared";
import { useState, useEffect } from "react";
import { Button, Spin } from "../components/admin-ui";
import { StatusBadge } from "../components/StatusBadge";
import {
  ArrowLeftOutlined,
  BankOutlined,
  FileTextOutlined,
  TagOutlined,
  UserOutlined,
  ClockCircleOutlined,
  DownloadOutlined,
  RedoOutlined,
  CheckCircleOutlined,
  PhoneOutlined,
  MailOutlined,
  CloseCircleOutlined,
  CheckOutlined,
} from "../components/admin-icons";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import dayjs from "dayjs";
import { formatOrderDate, formatOrderTime } from "../utils/formatLocaleDate";
import { issueRefundPath } from "../utils/refundNavigation";
import { formatTransactionId } from "../utils/transactionUi";
import { AdminShell } from "../layouts/AdminShell";
import { useAdminTransaction } from "../api/hooks";
import { downloadReceipt, type ReceiptTxn } from "./ReceiptModal";
import { useElementHeight } from "../hooks/useElementHeight";

function paymentMethodLabel(t: TFunction, method: string): string {
  const key = `orderDetail.payment.${method}`;
  const tr = t(key);
  return tr === key ? method : tr;
}

const TXN_DETAIL_PAGE_CLASS = "min-h-[calc(100vh-64px)] pb-24 max-[1280px]:!px-0";
const TXN_DETAIL_TOPBAR_CLASS = "fixed right-0 top-16 z-40 flex items-center justify-between gap-3 border-b border-[#e5e7eb] bg-white px-8 py-3.5 shadow-[0_8px_20px_rgba(25,28,29,0.03)] left-64 max-[900px]:left-[76px] max-[900px]:px-4 min-[901px]:max-[1280px]:left-[220px] min-[901px]:max-[1280px]:px-5";
const TXN_DETAIL_GRID_CLASS = "mx-auto grid items-start gap-10 px-8 py-7 grid-cols-[minmax(0,820px)_minmax(280px,1fr)] max-[900px]:grid-cols-1 max-[900px]:px-4 max-[900px]:py-5 min-[901px]:max-[1280px]:grid-cols-[minmax(0,1fr)] min-[901px]:max-[1280px]:px-5";
const TXN_DETAIL_CARD_CLASS = "flex w-full flex-col rounded-[32px] border border-transparent bg-white p-8 shadow-[0_12px_16px_rgba(25,28,29,0.04)]";
const TXN_DETAIL_SIDE_PANEL_CLASS = "sticky top-24 flex flex-col gap-5 max-[1280px]:static";
const TXN_DETAIL_FOOTER_BAR_CLASS = "fixed bottom-0 z-[100] flex justify-end rounded-t-[32px] bg-[rgba(255,255,255,0.9)] px-6 py-4 shadow-[0_-12px_32px_rgba(25,28,29,0.04)] backdrop-blur-[6px] left-[288px] w-[calc(100vw_-_320px)] max-[900px]:left-[92px] max-[900px]:w-[calc(100vw_-_108px)] max-[900px]:rounded-t-[20px] max-[900px]:p-4 min-[901px]:max-[1280px]:left-[240px] min-[901px]:max-[1280px]:w-[calc(100vw_-_260px)] min-[901px]:max-[1280px]:p-5";
const TXN_DETAIL_FOOTER_INNER_CLASS = "flex w-full max-w-[1480px] items-center justify-end gap-3 bg-transparent p-0 max-[900px]:flex-wrap max-[900px]:justify-start";
const TXN_DETAIL_FOOTER_BUTTON_CLASS = "!h-12 !rounded-xl !px-6 !text-[13px] !font-extrabold !uppercase !tracking-[0.8px] max-[900px]:!h-11 max-[900px]:!px-4 max-[900px]:!text-[12px]";
function getEventStage(evId: string, action: string) {
  if (action === "refund.approve" || action === "refund.reject") return 4;
  if (action === "refund.create") return 3;
  if (evId === "paid") return 2;
  if (evId === "initiated") return 1;
  return 5; // Default for others
}

function buildTimeline(txn: NonNullable<ReturnType<typeof useAdminTransaction>["data"]>, t: TFunction, lng: string) {
  type TimelineEvent = { id: string; ts: string; title: string; desc: string; tone: "default" | "error"; action: string };
  const events: TimelineEvent[] = [];

  const ACTION_LABELS: Record<string, string> = {
    "refund.create":  "transactionDetail.timelineRefundInitiated",
    "refund.approve": "transactionDetail.timelineRefundApproved",
    "refund.reject":  "transactionDetail.timelineRefundRejected",
    "order.admin_cancel": "transactionDetail.timelineOrderCancelled",
  };
  for (const log of txn.activity_logs ?? []) {
    // Skip order-related activity logs that aren't explicitly mapped in ACTION_LABELS
    if (log.action.startsWith("order.") && !ACTION_LABELS[log.action]) {
      continue;
    }
    const labelKey = ACTION_LABELS[log.action];
    const title = labelKey ? t(labelKey) : log.action.replace(/\./g, " ").replace(/\b\w/g, c => c.toUpperCase());
    
    let metaDesc = "";
    if (log.action.startsWith("refund.") && log.meta) {
      const { type, amount, method, note, transfer_time, transfer_method } = log.meta as Record<string, any>;
      
      // Refund initiated
      if (type && amount && method && log.action === "refund.create") {
        const typeLabel = type === "partial" ? t("transactionDetail.refundTypePartial") : type === "full" ? t("transactionDetail.refundTypeFull") : type;
        const methodLabel = paymentMethodLabel(t, method);
        metaDesc = t("transactionDetail.refundMetaDesc", {
          type: typeLabel,
          amount: Number(amount).toFixed(2),
          method: methodLabel
        });
      }
      
      // Refund approved or rejected
      if (log.action === "refund.approve" || log.action === "refund.reject") {
        const parts = [];
        if (note) parts.push(`${t("transactionDetail.timelineNote")}: ${note}`);
        if (transfer_time) parts.push(`${t("transactionDetail.timelineTransferTime")}: ${formatDateTimeWithAmPm(transfer_time, lng)}`);
        if (transfer_method) parts.push(`${t("transactionDetail.timelineTransferMethod")}: ${paymentMethodLabel(t, transfer_method)}`);
        metaDesc = parts.join(", ");
      }
    }

    if (!metaDesc && log.meta && !(log.action.startsWith("refund."))) {
      metaDesc = Object.entries(log.meta)
          .filter(([k]) => !["orderId", "prevStatus"].includes(k))
          .map(([k, v]) => `${k}: ${String(v)}`)
          .join(", ");
    }
    
    const isError = log.action.includes("reject") || log.action.includes("cancel");
    events.push({ id: log.id, ts: log.created_at, title, desc: metaDesc, tone: isError ? "error" : "default", action: log.action });
  }

  if (txn.paid_at) {
    events.push({
      id: "paid",
      ts: txn.paid_at,
      title: t("transactionDetail.timelinePaid"),
      desc: "",
      tone: "default",
      action: "paid",
    });
  }

  events.push({
    id: "initiated",
    ts: txn.created_at,
    title: t("transactionDetail.timelineInitiated"),
    desc: txn.order?.customer?.name ? t("transactionDetail.checkoutBy", { name: txn.order.customer.name }) : "",
    tone: "default",
    action: "initiated",
  });

  events.sort((a, b) => {
    const stageA = getEventStage(a.id, a.action);
    const stageB = getEventStage(b.id, b.action);
    
    const diff = dayjs(b.ts).valueOf() - dayjs(a.ts).valueOf();
    
    // If events happen very close to each other (within 60 seconds) and have a defined hierarchy,
    // force them to follow the logical business flow (e.g. Paid comes after Initiated).
    if (Math.abs(diff) < 60000 && stageA !== stageB) {
      return stageB - stageA;
    }
    
    if (diff !== 0) return diff;
    return stageB - stageA;
  });
  return events;
}

function SectionTitle({ icon, label, compact = false }: { icon: any; label: string; compact?: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <span className="flex size-8 items-center justify-center rounded-xl bg-[#fff7ed] text-[#ea580c]">
        {icon}
      </span>
      <span className={`${compact ? "text-[11px]" : "text-[13px]"} font-black uppercase tracking-[0.8px] text-[#534434]`}>
        {label}
      </span>
    </div>
  );
}

function InfoBlock({
  label,
  value,
  sub,
  icon,
  mono = false,
  success,
}: {
  label: string;
  value: any;
  sub?: string;
  icon?: any;
  mono?: boolean;
  success?: boolean;
}) {
  return (
    <div className="rounded-2xl border border-[#eef0f2] bg-[#fbfaf8] p-4">
      <div className="text-[10px] font-black uppercase tracking-[0.8px] text-[#867461]">{label}</div>
      <div className="mt-2 flex min-w-0 items-center gap-2">
        {icon && (
          <span className="flex size-8 shrink-0 items-center justify-center rounded-xl bg-white text-[#534434] shadow-[0_8px_20px_rgba(25,28,29,0.06)]">
            {icon}
          </span>
        )}
        {typeof success === "boolean" && (
          <span className={`size-2 shrink-0 rounded-full ${success ? "bg-[#16a34a]" : "bg-[#d6d3d1]"}`} />
        )}
        <span className={`min-w-0 truncate text-sm font-extrabold ${mono ? "font-mono" : ""} ${success ? "text-[#16a34a]" : "text-[#191c1d]"}`}>
          {value || "—"}
        </span>
      </div>
      {sub && <div className="mt-2 text-xs font-semibold text-[#867461]">{sub}</div>}
    </div>
  );
}

function BreakdownRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-2xl bg-[#fbfaf8] px-4 py-3 text-sm">
      <span className="font-semibold text-[#534434]">{label}</span>
      <span className="font-extrabold text-[#191c1d]">{value}</span>
    </div>
  );
}

function formatDateTimeWithAmPm(input: string, lng: string) {
  return `${formatOrderDate(input, lng)} ${formatOrderTime(input, lng)}`;
}

export function TransactionDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { search: qs } = useLocation();
  const [receiptDownloadedFromQuery, setReceiptDownloadedFromQuery] = useState(false);
  const [topbarRef, topbarHeight] = useElementHeight<HTMLDivElement>();

  const { data: txn, isLoading } = useAdminTransaction(id);

  const canRefund = txn?.status === "success" || txn?.status === "partial_refund";

  function handleDownloadReceipt() {
    if (!txn?.order?.kopitiam || !txn.order.items) return;
    downloadReceipt(txn as ReceiptTxn);
  }

  useEffect(() => {
    if (receiptDownloadedFromQuery || new URLSearchParams(qs).get("receipt") !== "1") return;
    if (!txn?.order?.kopitiam || !txn.order.items) return;
    downloadReceipt(txn as ReceiptTxn);
    setReceiptDownloadedFromQuery(true);
  }, [qs, receiptDownloadedFromQuery, txn]);

  if (isLoading) {
    return (
      <AdminShell>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
          <Spin size="large" />
        </div>
      </AdminShell>
    );
  }

  if (!txn) {
    return (
      <AdminShell>
        <div style={{ padding: 40, textAlign: "center", color: "#9CA3AF" }}>{t("transactionDetail.notFound")}</div>
      </AdminShell>
    );
  }

  const order = txn.order;
  const customer = order?.customer ?? null;
  const timeline = txn ? buildTimeline(txn, t, i18n.language) : [];
  const displayCode = formatTransactionId(txn.id);
  const sourceParam = new URLSearchParams(qs).get("from");
  const source = sourceParam === "orders" ? "orders" : "transactions";

  return (
    <>
    <AdminShell>
      <div className={TXN_DETAIL_PAGE_CLASS} style={{ paddingTop: topbarHeight ? topbarHeight + 24 : 57 }}>
        <div ref={topbarRef} className={TXN_DETAIL_TOPBAR_CLASS}>
          <div className="flex min-w-0 items-center gap-3">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
              className="!h-10 !w-10 !rounded-xl !border-none !bg-transparent !p-0 !text-[#534434]"
            />
            <div className="min-w-0">
              <div className="truncate text-xs font-bold uppercase tracking-[0.8px] text-[#867461]">{t("transactionDetail.breadcrumb")}</div>
              <div className="truncate text-base font-black text-[#191c1d]">{displayCode}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11px] font-black uppercase tracking-[0.8px] text-[#867461]">{t("transactionDetail.totalAmount")}</div>
            <div className="text-xl font-black text-[#ea580c]">RM {Number(txn.amount).toFixed(2)}</div>
          </div>
        </div>

        <div className={TXN_DETAIL_GRID_CLASS}>
          <div className="flex min-w-0 flex-col gap-5">
            <div className={`${TXN_DETAIL_CARD_CLASS} gap-7`}>
              <div className="flex justify-between gap-6 max-[900px]:flex-col">
                <div className="flex min-w-0 flex-col gap-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge status={txn.status} kind="transaction" size="md" />
                    <StatusBadge status={txn.method} label={paymentMethodLabel(t, txn.method)} size="md" />
                  </div>
                  <div className="text-3xl font-extrabold leading-tight text-[#191c1d]">{displayCode}</div>
                  <div className="flex flex-wrap items-center gap-3 text-sm uppercase text-[#534434]">
                    <span className="flex items-center gap-1">
                      <ClockCircleOutlined />
                      {formatDateTimeWithAmPm(txn.paid_at ?? txn.created_at, i18n.language)}
                    </span>
                    {order && (
                      <button
                        type="button"
                        onClick={() => navigate(`/orders/${order.id}`, { state: { activeNav: "/orders" } })}
                        className="border-none bg-transparent p-0 text-sm font-bold uppercase text-[#ea580c]"
                      >
                        {t("transactionDetail.orderIdLabel")} #{order.order_code}
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex min-w-[220px] flex-col justify-center rounded-2xl bg-[rgba(243,244,245,0.5)] p-5 text-right">
                  <div className="text-xs font-bold uppercase tracking-[1.2px] text-[#867461]">{t("transactionDetail.totalAmount")}</div>
                  <div className="mt-2 text-[32px] font-extrabold leading-none text-[#ea580c]">RM {Number(txn.amount).toFixed(2)}</div>
                </div>
              </div>
            </div>

            <div className={`${TXN_DETAIL_CARD_CLASS} !px-7 !py-6`}>
              <SectionTitle icon={<BankOutlined />} label={t("transactionDetail.paymentMethod")} />
              <div className="mt-5 grid gap-4 sm:grid-cols-3">
                <InfoBlock label={t("transactionDetail.colProvider")} value={paymentMethodLabel(t, txn.method)} icon={<BankOutlined />} />
                <InfoBlock label={t("transactionDetail.colReferenceId")} value={txn.reference ?? "—"} sub={t("transactionDetail.merchantReference")} mono />
                <InfoBlock
                  label={t("transactionDetail.colAuthorization")}
                  value={txn.status === "success" || txn.status === "partial_refund" ? t("transactionDetail.authSuccess") : txn.status.charAt(0).toUpperCase() + txn.status.slice(1)}
                  success={txn.status === "success" || txn.status === "partial_refund"}
                />
              </div>
            </div>

            {order && (
              <div className={`${TXN_DETAIL_CARD_CLASS} !px-7 !py-6`}>
                <SectionTitle icon={<FileTextOutlined />} label={t("transactionDetail.paymentBreakdown")} />
                <div className="mt-5 flex flex-col gap-4">
                  <BreakdownRow label={t("transactionDetail.subtotal")} value={`RM ${Number(order.subtotal).toFixed(2)}`} />
                  {Number(order.delivery_fee) > 0 && <BreakdownRow label={t("transactionDetail.deliveryFee")} value={`RM ${Number(order.delivery_fee).toFixed(2)}`} />}
                  {Number(order.sst_amount) > 0 && !(order as { sst_included?: boolean }).sst_included && (
                    <BreakdownRow
                      label={t("transactionDetail.taxSst", { rate: (() => { const r = Number((order as { sst_rate?: string }).sst_rate ?? 0.06); return r > 0 && r < 1 ? Math.round(r * 10000) / 100 : r; })() })}
                      value={`RM ${Number(order.sst_amount).toFixed(2)}`}
                    />
                  )}
                  {Number(order.voucher_discount) > 0 && (
                    <div className="flex justify-between text-sm text-[#006c49]">
                      <span className="flex items-center gap-1.5">
                        <TagOutlined className="text-[13px]" />
                        {t("orders.discountWithCode", { code: order.voucher_code ?? t("orders.voucherFallback") })}
                      </span>
                      <span className="font-bold text-[#ea580c]">-RM {Number(order.voucher_discount).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex items-baseline justify-between border-t border-[#e5e7eb] pt-4">
                    <span className="text-[15px] font-extrabold text-[#191c1d]">{t("transactionDetail.finalPaidAmount")}</span>
                    <span className="text-[22px] font-extrabold text-[#ea580c]">RM {Number(order.total).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}

            {txn.refunds.length > 0 && (
              <div className={`${TXN_DETAIL_CARD_CLASS} !px-7 !py-6`}>
                <SectionTitle icon={<RedoOutlined />} label={t("orderDetail.refundHistory")} />
                <div className="mt-5 flex flex-col gap-2.5">
                  {txn.refunds.map(refund => {
                    return (
                      <div key={refund.id} className="flex items-center justify-between rounded-[10px] border border-[#f3f4f6] bg-[#fafafa] px-4 py-3.5">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold leading-[1.3] text-[#191c1d]">
                            {refund.type === "partial" ? t("transactionDetail.partialRefundIssued") : t("transactionDetail.fullRefundIssued")}
                          </div>
                          <div className="mt-1 text-xs text-[#867461]">
                            {formatDateTimeWithAmPm(refund.created_at, i18n.language)}
                            {" · "}
                            <span className="font-semibold uppercase">{paymentMethodLabel(t, refund.method)}</span>
                          </div>
                          <div className="mt-2 text-sm text-[#534434]">{cancelReasonLabel(refund.reason, i18n.language)}</div>
                        </div>
                        <div className="ml-4 shrink-0 text-right">
                          <div className="text-[15px] font-bold text-[#dc2626]">-RM {Number(refund.amount).toFixed(2)}</div>
                          <StatusBadge status={refund.status} kind="refund" className="mt-1" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className={TXN_DETAIL_SIDE_PANEL_CLASS}>
            <div className="overflow-hidden rounded-3xl border border-[#e5e7eb] bg-white shadow-[0_12px_16px_rgba(25,28,29,0.04)]">
              <div className="h-1 bg-[#006c49]" />
              <div className="px-5 pb-5 pt-4">
                <SectionTitle icon={<UserOutlined />} label={t("transactionDetail.customerInfo")} compact />
                {customer ? (
                  <div className="mt-4">
                    <div className="mb-4 flex items-center gap-3">
                      <div className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-[#f3f4f5] text-base font-bold text-[#191c1d]">
                        {(customer.name ?? customer.phone).slice(0, 1).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="truncate text-base font-bold uppercase leading-none text-[#191c1d]">{customer.name ?? "—"}</div>
                        <div className="mt-1 text-xs text-[#867461]">{t("orderDetail.customerShortId", { id: customer.id.slice(0, 4).toUpperCase() })}</div>
                      </div>
                    </div>
                    <div className="mb-4 flex flex-col gap-2 text-[13px] text-[#534434]">
                      <span className="flex items-center gap-2"><PhoneOutlined />{customer.phone}</span>
                      {customer.email && <span className="flex items-center gap-2"><MailOutlined />{customer.email}</span>}
                    </div>
                  </div>
                ) : (
                  <div className="py-4 text-[13px] text-[#867461]">{t("transactionDetail.noCustomer")}</div>
                )}
                {order && (
                  <Button
                    block
                    className="!h-10 !rounded-lg !border !border-[#e5e7eb] !text-sm !font-semibold"
                    onClick={() => navigate(`/orders/${order.id}`, { state: { activeNav: "/orders" } })}
                  >
                    {t("transactionDetail.viewOrderDetails")}
                  </Button>
                )}
              </div>
            </div>

            <div className="rounded-3xl border border-[#e5e7eb] bg-white p-6 shadow-[0_12px_16px_rgba(25,28,29,0.04)]">
              <SectionTitle icon={<ClockCircleOutlined />} label={t("transactionDetail.transactionHistory")} compact />
              <div className="mt-6">
                <div className="flex flex-col gap-0">
                  {timeline.map((ev, idx) => {
                    const isLast = idx === timeline.length - 1;
                    const dotClass = ev.tone === "error"
                      ? "bg-[#ef4444] shadow-[0_0_0_4px_rgba(239,68,68,0.15)]"
                      : "bg-[#10b981] shadow-[0_0_0_4px_rgba(16,185,129,0.15)]";
                    return (
                      <div key={ev.id} className="group flex gap-4">
                        <div className="flex flex-col items-center">
                          <div className={`z-10 mt-1 size-2.5 rounded-full ${dotClass}`} />
                          {!isLast && <div className="my-2 w-[2px] flex-1 rounded-full bg-[#f1f5f9] transition-colors group-hover:bg-[#e2e8f0]" />}
                        </div>
                        <div className={`flex-1 ${!isLast ? "pb-6" : ""}`}>
                          <div className="mb-1 text-[10px] font-black uppercase tracking-[0.8px] text-[#94a3b8]">
                            {formatDateTimeWithAmPm(ev.ts, i18n.language)}
                          </div>
                          <div className="text-sm font-extrabold text-[#0f172a]">
                            {ev.title}
                          </div>
                          {ev.desc && (
                            <div className="mt-2 rounded-xl bg-[#f8fafc] p-3 text-xs font-semibold leading-relaxed text-[#64748b]">
                              {ev.desc}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={TXN_DETAIL_FOOTER_BAR_CLASS}>
        <div className={TXN_DETAIL_FOOTER_INNER_CLASS}>
          <Button
            icon={<DownloadOutlined className="text-[15px]" />}
            className={`${TXN_DETAIL_FOOTER_BUTTON_CLASS} !border !border-transparent !bg-[#f3f4f5] !text-[#191c1d] shadow-none`}
            onClick={handleDownloadReceipt}
          >
            {t("transactionDetail.downloadReceipt")}
          </Button>
          <Button
            type="primary"
            icon={<CheckCircleOutlined className="text-base" />}
            disabled={!canRefund}
            onClick={() => navigate(issueRefundPath(id ?? "", source), { state: { activeNav: "/transactions" } })}
            className={`${TXN_DETAIL_FOOTER_BUTTON_CLASS} !border-none !bg-[#ea580c] !px-8 !text-white shadow-[0_10px_24px_rgba(249,115,22,0.24)] hover:!bg-[#c2410c]`}
          >
            {t("transactionDetail.issueRefund")}
          </Button>
        </div>
      </div>
    </AdminShell>

  </>
  );
}
